<template>
    <div class="idp-goals">
        <div class="goals-header">
            <div class="goals-title">
                <i class="bi bi-flag-fill"></i>
                <h3>Development Goals</h3>
            </div>
            <b-button v-if="canEdit" size="sm" variant="outline-success" @click="$emit('add')">
                <i class="bi bi-plus-lg"></i>
                <span class="btn-text">Add Goal</span>
            </b-button>
        </div>

        <div class="goals-timeline">
            <!-- 3 Month Goals -->
            <div class="goal-period">
                <div class="period-header three-month">
                    <div class="period-icon">
                        <i class="bi bi-calendar-week"></i>
                    </div>
                    <div class="period-info">
                        <h4>3 Month Goals</h4>
                        <span class="period-subtitle">Short-term focus</span>
                    </div>
                    <span class="goal-count">{{ threeMonthGoals.length }}</span>
                </div>
                <div class="period-body">
                    <div v-for="goal in threeMonthGoals" :key="goal.id" class="goal-card">
                        <div class="goal-main">
                            <img src="@/views/profile/img/target.svg" alt="" class="goal-icon" />
                            <span v-html="goal.goal" class="goal-text"></span>
                        </div>
                        <div class="goal-details">
                            <div class="detail-item how-achieve">
                                <span class="detail-label">How to achieve</span>
                                <p>{{ goal.how_can || '—' }}</p>
                            </div>
                            <div class="detail-item what-need">
                                <span class="detail-label">What I need</span>
                                <p>{{ goal.what_do_i_need || '—' }}</p>
                            </div>
                        </div>
                        <b-button v-if="canEdit" size="sm" variant="outline-warning" class="edit-goal-btn"
                            @click="$emit('edit', goal)">
                            <i class="bi bi-pencil"></i>
                        </b-button>
                    </div>
                    <div v-if="threeMonthGoals.length === 0" class="no-goals">
                        <p>No 3-month goals set yet.</p>
                    </div>
                </div>
            </div>

            <!-- 6 Month Goals -->
            <div class="goal-period">
                <div class="period-header six-month">
                    <div class="period-icon">
                        <i class="bi bi-calendar-month"></i>
                    </div>
                    <div class="period-info">
                        <h4>6 Month Goals</h4>
                        <span class="period-subtitle">Medium-term objectives</span>
                    </div>
                    <span class="goal-count">{{ sixMonthGoals.length }}</span>
                </div>
                <div class="period-body">
                    <div v-for="goal in sixMonthGoals" :key="goal.id" class="goal-card">
                        <div class="goal-main">
                            <img src="@/views/profile/img/target.svg" alt="" class="goal-icon" />
                            <span v-html="goal.goal" class="goal-text"></span>
                        </div>
                        <div class="goal-details">
                            <div class="detail-item how-achieve">
                                <span class="detail-label">How to achieve</span>
                                <p>{{ goal.how_can || '—' }}</p>
                            </div>
                            <div class="detail-item what-need">
                                <span class="detail-label">What I need</span>
                                <p>{{ goal.what_do_i_need || '—' }}</p>
                            </div>
                        </div>
                        <b-button v-if="canEdit" size="sm" variant="outline-warning" class="edit-goal-btn"
                            @click="$emit('edit', goal)">
                            <i class="bi bi-pencil"></i>
                        </b-button>
                    </div>
                    <div v-if="sixMonthGoals.length === 0" class="no-goals">
                        <p>No 6-month goals set yet.</p>
                    </div>
                </div>
            </div>

            <!-- 12 Month Goals -->
            <div class="goal-period">
                <div class="period-header twelve-month">
                    <div class="period-icon">
                        <i class="bi bi-calendar-range"></i>
                    </div>
                    <div class="period-info">
                        <h4>12 Month Goals</h4>
                        <span class="period-subtitle">Long-term vision</span>
                    </div>
                    <span class="goal-count">{{ twelveMonthGoals.length }}</span>
                </div>
                <div class="period-body">
                    <div v-for="goal in twelveMonthGoals" :key="goal.id" class="goal-card">
                        <div class="goal-main">
                            <img src="@/views/profile/img/target.svg" alt="" class="goal-icon" />
                            <span v-html="goal.goal" class="goal-text"></span>
                        </div>
                        <div class="goal-details">
                            <div class="detail-item how-achieve">
                                <span class="detail-label">How to achieve</span>
                                <p>{{ goal.how_can || '—' }}</p>
                            </div>
                            <div class="detail-item what-need">
                                <span class="detail-label">What I need</span>
                                <p>{{ goal.what_do_i_need || '—' }}</p>
                            </div>
                        </div>
                        <b-button v-if="canEdit" size="sm" variant="outline-warning" class="edit-goal-btn"
                            @click="$emit('edit', goal)">
                            <i class="bi bi-pencil"></i>
                        </b-button>
                    </div>
                    <div v-if="twelveMonthGoals.length === 0" class="no-goals">
                        <p>No 12-month goals set yet.</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'IDPGoals',
    props: {
        goals: {
            type: Array,
            default: () => []
        },
        canEdit: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        threeMonthGoals() {
            return this.goals.filter(g => g.month_category === '3');
        },
        sixMonthGoals() {
            return this.goals.filter(g => g.month_category === '6');
        },
        twelveMonthGoals() {
            return this.goals.filter(g => g.month_category === '12');
        }
    }
};
</script>

<style scoped lang="scss">
.idp-goals {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 1.5rem;
}

.goals-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1.25rem;

    .btn-text {
        margin-left: 0.25rem;
    }
}

.goals-title {
    display: flex;
    align-items: center;
    gap: 0.625rem;

    i {
        font-size: 1.5rem;
        color: #8b5cf6;
    }

    h3 {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 600;
        color: #1a202c;
    }
}

.goals-timeline {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.goal-period {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    overflow: hidden;
}

.period-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem;
    color: #fff;

    &.three-month {
        background: linear-gradient(135deg, #3b82f6, #1d4ed8);
    }

    &.six-month {
        background: linear-gradient(135deg, #8b5cf6, #6d28d9);
    }

    &.twelve-month {
        background: linear-gradient(135deg, #14b8a6, #0d9488);
    }
}

.period-icon {
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;

    i {
        font-size: 1.25rem;
    }
}

.period-info {
    flex: 1;

    h4 {
        margin: 0;
        font-size: 1rem;
        font-weight: 600;
    }

    .period-subtitle {
        font-size: 0.75rem;
        opacity: 0.85;
    }
}

.goal-count {
    background: rgba(255, 255, 255, 0.25);
    padding: 0.25rem 0.75rem;
    border-radius: 12px;
    font-size: 0.875rem;
    font-weight: 600;
}

.period-body {
    padding: 1rem;
}

.goal-card {
    position: relative;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 0.75rem;

    &:last-child {
        margin-bottom: 0;
    }
}

.goal-main {
    display: flex;
    align-items: flex-start;
    gap: 0.625rem;
    margin-bottom: 0.75rem;
    padding-right: 2.5rem;
}

.goal-icon {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    margin-top: 2px;
}

.goal-text {
    font-size: 0.9375rem;
    font-weight: 500;
    color: #334155;
    line-height: 1.5;
}

.goal-details {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
}

.detail-item {
    background: #f1f5f9;
    padding: 0.75rem;
    border-radius: 6px;

    .detail-label {
        display: block;
        font-size: 0.6875rem;
        font-weight: 600;
        color: #64748b;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        margin-bottom: 0.375rem;
    }

    p {
        margin: 0;
        font-size: 0.875rem;
        color: #475569;
        line-height: 1.5;
    }
}

.edit-goal-btn {
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
}

.no-goals {
    text-align: center;
    padding: 1.5rem;
    color: #94a3b8;

    p {
        margin: 0;
        font-size: 0.875rem;
        font-style: italic;
    }
}

/* Mobile */
@media (max-width: 768px) {
    .idp-goals {
        padding: 1rem;
    }

    .goals-header {
        flex-direction: column;
        align-items: flex-start;

        button {
            width: 100%;
            justify-content: center;
        }
    }

    .period-header {
        padding: 0.875rem;
    }

    .period-icon {
        width: 36px;
        height: 36px;

        i {
            font-size: 1.125rem;
        }
    }

    .period-info h4 {
        font-size: 0.9375rem;
    }

    .goal-details {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 480px) {
    .period-header {
        flex-wrap: wrap;
        gap: 0.5rem;
    }

    .period-icon {
        width: 32px;
        height: 32px;
    }

    .goal-count {
        margin-left: auto;
    }

    .period-body {
        padding: 0.75rem;
    }

    .goal-card {
        padding: 0.75rem;
    }

    .goal-main {
        padding-right: 2rem;
    }

    .edit-goal-btn {
        top: 0.5rem;
        right: 0.5rem;
        padding: 0.25rem 0.5rem;
    }
}
</style>
